<template>
  <div class="page-venues">
    <section class="mx-auto w-full max-w-7xl px-4 py-10 lg:px-8">
      <div class="rounded-[28px] border border-[#eadfd4] bg-white px-6 py-6 shadow-sm">
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Public venues</p>
        <h1 class="mt-2 font-serif text-[2.4rem] font-semibold text-ink">Browse venues</h1>
        <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted">
          <span v-if="locationLabel" class="inline-flex items-center gap-2">
            <i class="ri-map-pin-2-line text-brand"></i>
            {{ locationLabel }}
          </span>
          <span v-if="searchKey" class="inline-flex items-center gap-2">
            <i class="ri-search-line text-brand"></i>
            {{ searchKey }}
          </span>
          <span v-if="eventType" class="inline-flex items-center gap-2">
            <i class="ri-calendar-event-line text-brand"></i>
            {{ eventType }}
          </span>
        </div>
      </div>

      <div class="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.55fr)_420px]">
        <div class="space-y-4">
          <div v-if="loading" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-6 text-sm font-semibold text-muted">
            Loading venues...
          </div>
          <div v-else-if="error" class="rounded-[24px] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
            {{ error }}
          </div>
          <div v-else-if="filteredVenues.length" class="grid gap-4 md:grid-cols-2">
            <article
              v-for="venue in filteredVenues"
              :key="venue.id"
              class="overflow-hidden rounded-[24px] border border-[#eadfd4] bg-white shadow-[0_10px_30px_rgba(102,73,44,0.05)] transition hover:-translate-y-1 hover:border-[#d9b594] hover:shadow-[0_18px_40px_rgba(102,73,44,0.12)]"
            >
              <div class="relative h-52 overflow-hidden bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)]">
                <img v-if="venue.image" :src="venue.image" :alt="venue.title" class="h-full w-full object-cover">
                <div v-else class="flex h-full items-center justify-center text-[2.4rem] font-semibold text-[#c69225]">
                  {{ venue.title.slice(0, 1) }}
                </div>
                <span v-if="venue.distanceLabel" class="absolute right-3 top-3 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-[#6b4d39] shadow-sm">
                  {{ venue.distanceLabel }}
                </span>
              </div>

              <div class="p-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <p class="text-xs font-semibold uppercase tracking-[0.16em] text-[#b78969]">{{ venue.typeLabel }}</p>
                    <h2 class="mt-2 text-[1.35rem] font-semibold text-ink">{{ venue.title }}</h2>
                    <p class="mt-2 text-sm text-[#7b624d]">{{ venue.tagLine || 'Venue details available on inquiry.' }}</p>
                  </div>
                </div>

                <div class="mt-4 flex flex-wrap gap-2">
                  <span
                    v-for="item in venue.highlights.slice(0, 3)"
                    :key="item"
                    class="rounded-full bg-[#fbf1e6] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand"
                  >
                    {{ item }}
                  </span>
                </div>

                <div class="mt-4 space-y-2 text-sm text-muted">
                  <p class="inline-flex items-start gap-2">
                    <i class="ri-map-pin-line mt-0.5 text-brand"></i>
                    <span>{{ venue.address }}</span>
                  </p>
                  <p class="inline-flex items-center gap-2">
                    <i class="ri-calendar-2-line text-brand"></i>
                    <span>{{ venue.estLabel }}</span>
                  </p>
                </div>

                <div class="mt-5 flex justify-end">
                  <RouterLink
                    :to="{ name: 'venue-invite-detail', params: { venueId: venue.id }, query: detailQuery }"
                    class="inline-flex items-center rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition hover:bg-brandDark"
                  >
                    View venue
                  </RouterLink>
                </div>
              </div>
            </article>
          </div>
          <div v-else class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-6 text-sm text-muted">
            No venues found.
          </div>
        </div>

        <div class="overflow-hidden rounded-[24px] border border-[#eadfd4] bg-white shadow-sm">
          <div id="venues-map" class="h-[460px] w-full"></div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import api from '@/services/api'
import { useSearchStore } from '@/stores/search'

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

const route = useRoute()
const searchStore = useSearchStore()

const venues = ref([])
const loading = ref(true)
const error = ref('')
const map = ref(null)
const markers = ref([])
const locationRequested = ref(false)

const requestId = computed(() => String(route.query.id || '').trim())
const latitude = computed(() => String(route.query.latitude || route.query.lat || searchStore.lat || '').trim())
const longitude = computed(() => String(route.query.longitude || route.query.lng || searchStore.lng || '').trim())
const eventType = computed(() => String(route.query.event_type || '').trim())
const locationLabel = computed(() => String(route.query.location || searchStore.locationText || '').trim())
const searchKey = computed(() => String(route.query.search_key || '').trim())
const detailQuery = computed(() => ({
  location: route.query.location || undefined,
  search_key: route.query.search_key || undefined,
  id: route.query.id || undefined,
  event_type: route.query.event_type || undefined,
  latitude: latitude.value || undefined,
  longitude: longitude.value || undefined
}))

const normalizeVenue = (item = {}, index = 0) => ({
  id: String(item?.id || item?.venue_id || `venue-${index}`),
  title: String(item?.title || item?.name || 'Untitled venue').trim(),
  image: String(item?.cover_image || item?.image || '').trim(),
  tagLine: String(item?.tag_line || item?.tagLine || '').trim(),
  about: String(item?.about || '').trim(),
  address: String(item?.address || 'Location unavailable').trim(),
  latitude: Number(item?.latitude),
  longitude: Number(item?.longitude),
  highlights: String(item?.highlights || '').split(',').map((entry) => entry.trim()).filter(Boolean),
  typeLabel: String(item?.venue_type || item?.type || 'Venue').trim() || 'Venue',
  estLabel: item?.est_year ? `Est. ${item.est_year}` : 'Year not specified',
  distanceLabel: item?.distance ? `${Number(item.distance).toFixed(1)} km away` : ''
})

const filteredVenues = computed(() => {
  const query = searchKey.value.toLowerCase()
  if (!query) return venues.value
  return venues.value.filter((venue) => {
    const haystack = [
      venue.title,
      venue.tagLine,
      venue.about,
      venue.address,
      venue.typeLabel,
      ...venue.highlights
    ].join(' ').toLowerCase()
    return haystack.includes(query)
  })
})

const loadVenues = async () => {
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('id', requestId.value || '')
    formData.append('latitude', latitude.value || '')
    formData.append('longitude', longitude.value || '')
    formData.append('event_type', eventType.value || '')
    const { data } = await api.getVenuesInvite(formData)
    const items = Array.isArray(data?.data) ? data.data : Array.isArray(data?.list) ? data.list : []
    venues.value = items.map(normalizeVenue)
  } catch (err) {
    error.value = err?.message || 'Failed to load venues.'
    venues.value = []
  } finally {
    loading.value = false
  }
}

const ensureMap = () => {
  if (map.value) return
  map.value = L.map('venues-map').setView([20.5937, 78.9629], 5)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors'
  }).addTo(map.value)
}

const dropMarkers = () => {
  if (!map.value) return
  markers.value.forEach((marker) => marker.remove())
  markers.value = []

  const markerVenues = filteredVenues.value.filter((item) => Number.isFinite(item.latitude) && Number.isFinite(item.longitude))
  markerVenues.forEach((venue) => {
    const marker = L.marker([venue.latitude, venue.longitude]).addTo(map.value)
    marker.bindPopup(`<strong>${venue.title}</strong><br>${venue.address}`)
    markers.value.push(marker)
  })

  if (markers.value.length) {
    const group = L.featureGroup(markers.value)
    map.value.fitBounds(group.getBounds().pad(0.2))
  } else {
    map.value.setView([20.5937, 78.9629], 5)
  }
}

const ensureLocation = async () => {
  if (locationRequested.value) return
  if (latitude.value && longitude.value) return
  locationRequested.value = true
  if (typeof navigator === 'undefined' || !navigator.geolocation) return
  await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        searchStore.setLocation({
          text: locationLabel.value || 'Current location',
          lat: position.coords.latitude,
          lng: position.coords.longitude
        })
        resolve()
      },
      () => resolve(),
      { enableHighAccuracy: true, timeout: 8000 }
    )
  })
}

onMounted(async () => {
  ensureMap()
  await ensureLocation()
  await loadVenues()
  dropMarkers()
})

watch(
  () => [route.fullPath, searchStore.lat, searchStore.lng],
  async () => {
    await loadVenues()
    dropMarkers()
  }
)

watch(filteredVenues, () => {
  dropMarkers()
})
</script>

<style scoped>
.page-venues {
  min-height: 100vh;
}
</style>
